import { useEffect, useMemo, useState } from 'react'
import { X, AlertTriangle, Shuffle } from 'lucide-react'
import { Studio } from '@/types'
import { getSupabase } from '@/lib/supabase'
import { NumberInput } from '@/components/ui/FormComponents'
import { formatCurrency } from '@/lib/utils'
import { useNavigate } from 'react-router-dom'

interface Props {
  isOpen: boolean
  onClose: () => void
  onSaved?: (savedValue?: number) => void
  studios: Studio[]
  selectedDate: string
  userId?: string | null
  currentStudioId?: number
  currentPartnerNumber?: number | null
  currentPeriodId?: number | null
  /** DB field name in studio_daily_totals */
  dbField: string
  /** Modal title */
  title: string
  /** Label for the total-amount input */
  totalLabel: string
  /** If provided, values are displayed as currency; otherwise as plain numbers */
  currency?: string
}

type Step = 'form' | 'confirm'
type TemplateStatus = 'idle' | 'loading' | 'enabled' | 'disabled' | 'missing'

function roundToTwoDecimals(value: number) {
  return Math.round((value + Number.EPSILON) * 100) / 100
}

function buildAllocationGroupKey(studioIds: number[]) {
  return [...studioIds].sort((a, b) => a - b).join(',')
}

export function BulkMetricModal({
  isOpen,
  onClose,
  onSaved,
  studios,
  selectedDate,
  userId,
  currentStudioId,
  currentPartnerNumber,
  currentPeriodId,
  dbField,
  title,
  totalLabel,
  currency,
}: Props) {
  const navigate = useNavigate()
  // Уникальные города
  const allCities = useMemo(() => {
    const map = new Map<number, string>()
    studios.forEach((s) => {
      const id = s.city_id ?? s.city?.city_id
      const name = s.city?.city_name ?? String(id ?? '?')
      if (id != null) map.set(id, name)
    })
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'ru'))
  }, [studios])

  const [selectedCities, setSelectedCities] = useState<Set<number>>(() => new Set(allCities.map((c) => c.id)))
  const [percentages, setPercentages] = useState<Record<number, number | undefined>>({})
  const [totalAmount, setTotalAmount] = useState<number | undefined>()
  const [step, setStep] = useState<Step>('form')
  const [existingValues, setExistingValues] = useState<Record<number, number | null>>({})
  const [loadingCheck, setLoadingCheck] = useState(false)
  const [loadingTemplate, setLoadingTemplate] = useState(false)
  const [templateStatus, setTemplateStatus] = useState<TemplateStatus>('idle')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  // Студии с учётом фильтра по городам, отсортированные по городу → номеру студии
  const filteredStudios = useMemo(
    () => studios
      .filter((s) => {
        const id = s.city_id ?? s.city?.city_id
        return id != null && selectedCities.has(id)
      })
      .sort((a, b) => {
        const cityA = a.city?.city_name ?? ''
        const cityB = b.city?.city_name ?? ''
        const cityDiff = cityA.localeCompare(cityB, 'ru')
        if (cityDiff !== 0) return cityDiff
        return (a.studio_number ?? '').localeCompare(b.studio_number ?? '', 'ru', { numeric: true })
      }),
    [studios, selectedCities]
  )

  const allocationGroupKey = useMemo(
    () => buildAllocationGroupKey(filteredStudios.map((studio) => studio.studio_internal_id)),
    [filteredStudios]
  )

  const totalPercent = useMemo(
    () => filteredStudios.reduce((sum, s) => sum + (percentages[s.studio_internal_id] ?? 0), 0),
    [filteredStudios, percentages]
  )

  const isValid = Math.abs(totalPercent - 100) < 0.01 && (totalAmount ?? 0) > 0

  const studioValue = (studioId: number) => {
    const pct = percentages[studioId] ?? 0
    return totalAmount != null ? roundToTwoDecimals(totalAmount * pct / 100) : undefined
  }

  const formatValue = (v: number) =>
    currency ? formatCurrency(v, currency) : (Number.isInteger(v) ? String(v) : v.toFixed(2).replace('.', ','))

  const studioLines = (studio: Studio) => {
    const top: string[] = []
    if (studio.studio_number) top.push(`Студия ${studio.studio_number}`)
    if (studio.city?.city_name) top.push(studio.city.city_name)
    return { top: top.join(' — ') || '—', bottom: studio.address ?? '' }
  }

  const toggleCity = (cityId: number) => {
    setSelectedCities((prev) => {
      const next = new Set(prev)
      if (next.has(cityId)) next.delete(cityId)
      else next.add(cityId)
      return next
    })
    // Сбрасываем проценты студий убранного города
    setPercentages((prev) => {
      const next = { ...prev }
      studios.forEach((s) => {
        const id = s.city_id ?? s.city?.city_id
        if (id === cityId) delete next[s.studio_internal_id]
      })
      return next
    })
  }

  useEffect(() => {
    if (!isOpen) return
    setSelectedCities(new Set(allCities.map((city) => city.id)))
    setPercentages({})
    setTotalAmount(undefined)
    setExistingValues({})
    setStep('form')
    setError(null)
    setSuccess(false)
  }, [isOpen, allCities])

  useEffect(() => {
    const loadTemplate = async () => {
      const supportsTemplate =
        dbField === 'marketing_expenses_amount' || dbField === 'leads_count'

      if (!isOpen || !supportsTemplate) return
      if (!currentPartnerNumber || !currentPeriodId || !allocationGroupKey) {
        setPercentages({})
        setTemplateStatus('missing')
        return
      }

      setLoadingTemplate(true)
      setTemplateStatus('loading')
      try {
        const supabase = getSupabase()
        const { data: weekData, error: weekError } = await (supabase as any)
          .from('metric_weeks')
          .select('week_number_in_period')
          .eq('period_id', currentPeriodId)
          .lte('week_start', selectedDate)
          .gte('week_end', selectedDate)
          .maybeSingle()

        if (weekError) throw weekError

        const selectedWeekNumber = weekData?.week_number_in_period
        if (!selectedWeekNumber) {
          setPercentages({})
          setTemplateStatus('missing')
          return
        }

        const { data, error: templateError } = await (supabase as any)
          .from('partner_marketing_allocation_plan')
          .select('studio_internal_id, allocation_percent, is_enabled')
          .eq('partner_number', currentPartnerNumber)
          .eq('period_id', currentPeriodId)
          .eq('week_number_in_period', selectedWeekNumber)
          .eq('allocation_group_key', allocationGroupKey)

        if (templateError) throw templateError

        const nextPercentages: Record<number, number | undefined> = {}
        filteredStudios.forEach((studio) => {
          nextPercentages[studio.studio_internal_id] = undefined
        })

        const rows = (data ?? []) as Array<{
          studio_internal_id: number
          allocation_percent: number | null
          is_enabled: boolean | null
        }>
        const hasRows = rows.length > 0
        const isEnabled = rows.some((row) => Boolean(row.is_enabled))

        if (hasRows && isEnabled) {
          rows.forEach((row) => {
            nextPercentages[row.studio_internal_id] =
              row.allocation_percent === null ? undefined : Number(row.allocation_percent)
          })
          setPercentages(nextPercentages)
          setTemplateStatus('enabled')
        } else {
          setPercentages({})
          setTemplateStatus(hasRows ? 'disabled' : 'missing')
        }
      } catch (e: any) {
        setError(e.message ?? 'Ошибка при загрузке шаблона распределения')
        setTemplateStatus('missing')
      } finally {
        setLoadingTemplate(false)
      }
    }

    loadTemplate()
  }, [isOpen, dbField, currentPartnerNumber, currentPeriodId, allocationGroupKey, filteredStudios, selectedDate])

  // Распределить остаток процентов по незаполненным ячейкам
  const distributeRemaining = () => {
    const unfilled = filteredStudios.filter((s) => percentages[s.studio_internal_id] === undefined)
    if (unfilled.length === 0) return
    const remaining = 100 - totalPercent
    if (remaining <= 0) return
    const share = Math.round((remaining / unfilled.length) * 100) / 100
    setPercentages((prev) => {
      const next = { ...prev }
      let assigned = 0
      unfilled.forEach((s, i) => {
        if (i === unfilled.length - 1) {
          next[s.studio_internal_id] = Math.round((remaining - assigned) * 100) / 100
        } else {
          next[s.studio_internal_id] = share
          assigned += share
        }
      })
      return next
    })
  }

  const studiosToBeSaved = useMemo(
    () => filteredStudios.filter((s) => percentages[s.studio_internal_id] !== undefined),
    [filteredStudios, percentages]
  )

  const overwrites = useMemo(
    () => studiosToBeSaved.filter((s) => {
      const existing = existingValues[s.studio_internal_id]
      return existing !== null && existing !== undefined && existing !== 0
    }),
    [studiosToBeSaved, existingValues]
  )

  const handleProceedToConfirm = async () => {
    if (!isValid) return
    setLoadingCheck(true)
    setError(null)
    try {
      const supabase = getSupabase()
      const ids = filteredStudios.map((s) => s.studio_internal_id)
      const { data, error: dbError } = await (supabase as any)
        .from('studio_daily_totals')
        .select(`studio_internal_id, ${dbField}`)
        .eq('metric_date', selectedDate)
        .in('studio_internal_id', ids)

      if (dbError) throw dbError

      const map: Record<number, number | null> = {}
      filteredStudios.forEach((s) => { map[s.studio_internal_id] = null })
      ;(data ?? []).forEach((row: any) => {
        map[row.studio_internal_id] = row[dbField] ?? null
      })
      setExistingValues(map)
      setStep('confirm')
    } catch (e: any) {
      setError(e.message ?? 'Ошибка при проверке данных')
    } finally {
      setLoadingCheck(false)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    setError(null)

    const supabase = getSupabase()
    const rows = studiosToBeSaved.map((studio) => ({
      studio_internal_id: studio.studio_internal_id,
      metric_date: selectedDate,
      [dbField]: studioValue(studio.studio_internal_id) ?? 0,
      last_saved_by_user_id: userId ?? null,
    }))

    const { error: dbError } = await (supabase as any)
      .from('studio_daily_totals')
      .upsert(rows, { onConflict: 'studio_internal_id,metric_date' })

    if (dbError) {
      setSaving(false)
      setError(dbError.message)
      return
    }

    setSaving(false)
    setSuccess(true)
    const currentStudioRow = currentStudioId != null
      ? rows.find((row) => row.studio_internal_id === currentStudioId)
      : undefined
    const savedValue = currentStudioRow?.[dbField]
    onSaved?.(typeof savedValue === 'number' ? savedValue : undefined)
    setTimeout(() => {
      setSuccess(false)
      setPercentages({})
      setTotalAmount(undefined)
      setExistingValues({})
      setStep('form')
      onClose()
    }, 1200)
  }

  const handleClose = () => {
    setPercentages({})
    setTotalAmount(undefined)
    setExistingValues({})
    setStep('form')
    setError(null)
    setSuccess(false)
    onClose()
  }

  const handleOpenTemplateSettings = () => {
    if (!currentPartnerNumber || !currentStudioId) return
    onClose()
    navigate(
      `/partners/${currentPartnerNumber}/studios/${currentStudioId}/studio-metrics-grid`,
      {
        state: {
          selectedMonth: selectedDate.slice(0, 7),
          useSelectedMonth: true,
        },
      }
    )
  }

  if (!isOpen) return null

  const unfilledCount = filteredStudios.filter((s) => percentages[s.studio_internal_id] === undefined).length
  const remaining = Math.round((100 - totalPercent) * 100) / 100
  const templateStatusMeta = {
    enabled: {
      badge: 'Шаблон подключен',
      tone: 'text-green-700 bg-green-50 border-green-200',
      text: 'Проценты для этого набора студий настроены в декомпозиции и подставлены автоматически. Если нужно, их можно изменить прямо здесь на текущую дату (это не повлияет на шаблон).'
    },
    disabled: {
      badge: 'Шаблон выключен',
      tone: 'text-amber-700 bg-amber-50 border-amber-200',
      text: 'Связка распределения % между студиями сохранена в декомпозиции, но автоподстановка сейчас выключена.'
    },
    missing: {
      badge: 'Шаблон не настроен',
      tone: 'text-muted-foreground bg-muted/30 border-border',
      text: 'Чтобы проценты подставлялись автоматически на весь период, настройте шаблон в декомпозиции. Здесь можно заполнить их вручную на текущую дату (это не повлияет на шаблон).'
    },
    loading: {
      badge: 'Проверяем шаблон',
      tone: 'text-muted-foreground bg-muted/30 border-border',
      text: 'Проверяем, есть ли сохраненный шаблон для выбранного набора студий.'
    },
    idle: {
      badge: '',
      tone: 'text-muted-foreground bg-muted/30 border-border',
      text: ''
    }
  } as const

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="w-full max-w-md bg-card rounded-xl border border-border my-8">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h3 className="text-base font-medium">{title}</h3>
          <button
            type="button"
            onClick={handleClose}
            className="p-1 rounded hover:bg-muted transition-colors"
            aria-label="Закрыть"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {step === 'form' && (
            <>
              <div className="text-sm text-muted-foreground">
                Дата: <span className="font-medium text-foreground">{selectedDate}</span>
              </div>

              {(dbField === 'marketing_expenses_amount' || dbField === 'leads_count') && (
                <div className={`rounded-lg border px-3 py-2 space-y-2 ${templateStatusMeta[templateStatus].tone}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-xs font-medium">
                      {templateStatusMeta[templateStatus].badge}
                    </div>
                    {currentPartnerNumber && currentStudioId && (
                      <button
                        type="button"
                        onClick={handleOpenTemplateSettings}
                        className="shrink-0 text-xs text-primary hover:text-primary/80 transition-colors"
                      >
                        Настроить в декомпозиции
                      </button>
                    )}
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-xs">
                      {templateStatusMeta[templateStatus].text}
                    </div>
                  </div>
                </div>
              )}

              <NumberInput
                label={totalLabel}
                value={totalAmount}
                onChange={setTotalAmount}
                inputMode="decimal"
                min={0}
                autoFocus
              />

              {/* Фильтр по городам */}
              {allCities.length > 1 && (
                <div className="space-y-1.5">
                  <div className="text-xs font-medium text-muted-foreground">Города</div>
                  <div className="flex flex-wrap gap-1.5">
                    {allCities.map(({ id, name }) => (
                      <button
                        key={id}
                        type="button"
                        onClick={() => toggleCity(id)}
                        className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                          selectedCities.has(id)
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'bg-transparent text-muted-foreground border-border hover:bg-muted'
                        }`}
                      >
                        {name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {filteredStudios.length > 0 ? (
                <div className="space-y-2">
                  {/* Заголовок с кнопкой распределения */}
                  <div className="flex items-center justify-between min-h-[20px]">
                    <div className="text-xs font-medium text-muted-foreground">Студии</div>
                    {unfilledCount > 0 && remaining > 0 && (
                      <button
                        type="button"
                        onClick={distributeRemaining}
                        className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors"
                      >
                        <Shuffle className="w-3 h-3" />
                        Распределить остаток ({remaining}% на {unfilledCount})
                      </button>
                    )}
                  </div>

                  {filteredStudios.map((studio) => {
                    const val = studioValue(studio.studio_internal_id)
                    const { top, bottom } = studioLines(studio)
                    return (
                      <div key={studio.studio_internal_id} className="flex items-center gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm truncate">{top}</div>
                          {bottom && <div className="text-xs text-muted-foreground truncate">{bottom}</div>}
                        </div>
                        <div className="w-24 shrink-0">
                          <NumberInput
                            label=""
                            value={percentages[studio.studio_internal_id]}
                            onChange={(v) =>
                              setPercentages((prev) => ({ ...prev, [studio.studio_internal_id]: v }))
                            }
                            inputMode="decimal"
                            min={0}
                            max={100}
                            placeholder="%"
                          />
                        </div>
                        <div className="w-20 shrink-0 text-right text-sm text-muted-foreground">
                          {val != null
                            ? formatValue(val)
                            : <span className="text-muted-foreground/40">—</span>}
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground text-center py-4">
                  Выберите хотя бы один город
                </div>
              )}

              <div className={`text-sm font-medium ${Math.abs(totalPercent - 100) < 0.01 ? 'text-green-600' : 'text-muted-foreground'}`}>
                Итого: {totalPercent.toFixed(totalPercent % 1 === 0 ? 0 : 2)}%
                {Math.abs(totalPercent - 100) >= 0.01 && totalPercent > 0 && (
                  <span className="ml-2 text-amber-600 font-normal">(нужно 100%)</span>
                )}
              </div>

              {error && <p className="text-sm text-red-600">{error}</p>}

              <div className="flex gap-2 pt-1">
                <button type="button" onClick={handleClose} className="flex-1 btn-secondary">
                  Отмена
                </button>
                <button
                  type="button"
                  onClick={handleProceedToConfirm}
                  className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!isValid || loadingCheck}
                >
                  {loadingCheck ? 'Проверка...' : 'Продолжить →'}
                </button>
              </div>
            </>
          )}

          {step === 'confirm' && (
            <>
              <div className={`rounded-lg border p-3 space-y-1.5 ${overwrites.length > 0 ? 'border-amber-300 bg-amber-50' : 'border-border bg-muted/50'}`}>
                {overwrites.length > 0 && (
                  <div className="flex items-center gap-2 text-xs font-medium text-amber-800 mb-2">
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                    Часть данных будет перезаписана
                  </div>
                )}
                {studiosToBeSaved.map((studio) => {
                  const newVal = studioValue(studio.studio_internal_id) ?? 0
                  const existing = existingValues[studio.studio_internal_id]
                  const isOverwrite = existing !== null && existing !== undefined && existing !== 0
                  const oldLabel = isOverwrite ? formatValue(existing ?? 0) : ''
                  const { top, bottom } = studioLines(studio)
                  return (
                    <div key={studio.studio_internal_id} className="flex items-start justify-between gap-2 text-sm">
                      <div className={`min-w-0 ${isOverwrite ? 'text-amber-800' : 'text-muted-foreground'}`}>
                        <div className="truncate">{top}</div>
                        {bottom && <div className="text-xs truncate opacity-75">{bottom}</div>}
                      </div>
                      <span className={`shrink-0 font-medium tabular-nums ${isOverwrite ? 'text-amber-900' : ''}`}>
                        {oldLabel && (
                          <><span className="text-muted-foreground font-normal">{oldLabel}</span>{' → '}</>
                        )}
                        {formatValue(newVal)}
                      </span>
                    </div>
                  )
                })}
              </div>

              {error && <p className="text-sm text-red-600">{error}</p>}
              {success && <p className="text-sm text-green-600">Сохранено!</p>}

              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => { setStep('form'); setError(null) }}
                  className="flex-1 btn-secondary"
                  disabled={saving}
                >
                  Назад
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={saving}
                >
                  {saving ? 'Сохранение...' : overwrites.length > 0 ? 'Подтвердить и сохранить' : 'Сохранить'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
